import type { FormularyResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { CheckCircle2, FileText, ShieldAlert, ShieldOff, ShieldQuestion } from 'lucide-react';

import { cn } from '@/lib/utils';
import { bucketFormularyStatus, formularyStatusLabel } from '../model/formularyDisplayPolicy';

export interface FormularyStatusBadgeProps {
  /** The formulary response from ``GET /dosespot/patients/{id}/formulary``. */
  formulary: FormularyResponseT | null | undefined;
  /** Whether the formulary lookup is still pending. */
  isLoading: boolean;
  /**
   * The formulary endpoint may return ``formulary=null`` when the
   * patient has no active coverage on the resolved channel — surface a
   * dedicated "no coverage" state in that case so the clinician knows
   * the lookup ran but found nothing rather than seeing a stale "On
   * Formulary" badge from a prior selection.
   */
  hasResponse: boolean;
}

interface StatusVisual {
  Icon: typeof ShieldAlert;
  label: string;
  description: string | null;
  className: string;
}

/**
 * Compact formulary-status indicator shown directly underneath the
 * medication selector on the Rx wizard's Details step.
 *
 * Renders right after a medication is selected — well before pharmacy
 * / quantity / days-supply have been filled in — so the clinician sees
 * "this drug is on/off formulary" while still in the medication picker
 * mental model. The richer alternatives + copay panel lives on the
 * Review step (:class:`BenefitsCoordinationPanel`).
 *
 * Status rendering uses the shared formulary display policy so the
 * details badge, review panel, and alternatives modal agree on payer
 * statuses like numeric "U" / "0" responses.
 */
export function FormularyStatusBadge({
  formulary,
  isLoading,
  hasResponse,
}: FormularyStatusBadgeProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground">
        <span className="size-3 animate-pulse rounded-full bg-muted-foreground/50" aria-hidden />
        Looking up formulary…
      </div>
    );
  }

  if (!hasResponse) {
    return null;
  }

  const visual =
    formulary == null
      ? noDataVisual()
      : chooseVisual({
          status: formulary.FormularyStatus ?? null,
          restrictions: formulary.Restrictions ?? null,
        });
  if (!visual) return null;

  const restrictionFlags = collectRestrictionFlags(formulary?.Restrictions ?? null);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          'inline-flex items-center gap-2 self-start rounded-md border px-2.5 py-1.5 text-xs font-medium',
          visual.className
        )}
        aria-live="polite"
      >
        <visual.Icon className="size-3.5" aria-hidden />
        <span>{visual.label}</span>
      </div>
      {visual.description ? (
        <span className="text-[11px] text-muted-foreground">{visual.description}</span>
      ) : null}
      {restrictionFlags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {restrictionFlags.map((flag) => (
            <span
              key={flag}
              className="rounded-full border border-amber-400/50 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
            >
              {flag}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function chooseVisual(args: {
  status: string | null;
  restrictions: FormularyResponseT['Restrictions'] | null | undefined;
}): StatusVisual | null {
  const { status: rawStatus, restrictions } = args;
  const status = formularyStatusLabel(rawStatus) ?? rawStatus;
  const bucket = bucketFormularyStatus(rawStatus, restrictions);
  const lower = status?.toLowerCase() ?? '';

  if (bucket === 'excluded') {
    return {
      Icon: ShieldOff,
      label: 'EXCLUDED',
      description: 'Drug is not covered by this plan.',
      className:
        'border-red-400/40 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-100',
    };
  }
  if (restrictions?.HasPriorAuthorization || lower.includes('pa required')) {
    return {
      Icon: ShieldAlert,
      label: status?.toUpperCase() ?? 'PRIOR AUTHORIZATION REQUIRED',
      description: 'A prior authorization will be initiated when sent.',
      className:
        'border-amber-400/40 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100',
    };
  }
  if (bucket === 'not-reimbursable') {
    return {
      Icon: ShieldOff,
      label: status?.toUpperCase() ?? 'NOT REIMBURSABLE',
      description: 'Drug is not reimbursable by this plan.',
      className:
        'border-red-400/40 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-100',
    };
  }
  if (bucket === 'non-formulary' || bucket === 'on-formulary-not-preferred') {
    return {
      Icon: FileText,
      label: status?.toUpperCase() ?? 'NON-FORMULARY',
      description: 'Drug is not on the preferred formulary list.',
      className:
        'border-orange-400/40 bg-orange-50 text-orange-900 dark:border-orange-500/30 dark:bg-orange-950/40 dark:text-orange-100',
    };
  }
  if (bucket === 'on-formulary-preferred') {
    return {
      Icon: CheckCircle2,
      label: status?.toUpperCase() ?? 'ON FORMULARY',
      description: null,
      className:
        'border-emerald-400/40 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-100',
    };
  }
  return {
    Icon: ShieldQuestion,
    label: 'FORMULARY STATUS UNKNOWN',
    description: 'Payer response did not confirm coverage.',
    className:
      'border-border/60 bg-muted/40 text-foreground/80 dark:border-border/40 dark:bg-muted/20 dark:text-foreground/80',
  };
}

function noDataVisual(): StatusVisual {
  return {
    Icon: ShieldQuestion,
    label: 'NO FORMULARY DATA',
    description: 'The payer did not return formulary details for this lookup.',
    className:
      'border-border/60 bg-muted/40 text-foreground/80 dark:border-border/40 dark:bg-muted/20 dark:text-foreground/80',
  };
}

function collectRestrictionFlags(
  restrictions: FormularyResponseT['Restrictions'] | null | undefined
): string[] {
  if (!restrictions) return [];
  const flags: string[] = [];
  if (restrictions.HasStepTherapy) flags.push('Step Therapy');
  if (restrictions.HasQuantityLimit) flags.push('Qty Limit');
  if (restrictions.HasAgeLimit) flags.push('Age Limit');
  if (restrictions.HasMedicalNecessity) flags.push('Med Necessity');
  return flags;
}
